import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import { tenantEntity } from '@/config/entities/tenants';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { toast } from 'react-hot-toast';
import { useLanguage } from '@/state/language/LanguageProvider';
import { GenericCRUDService } from '@/services/genericCRUD';

export const TenantsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  // Use Generic CRUD Service for tenant management
  const tenantService = new GenericCRUDService(tenantEntity);
  
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleCreate = () => {
    navigate('/tenants/create');
  };

  const handleView = (item: any) => {
    navigate(`/tenants/${item.id}`);
  };

  const handleEdit = (item: any) => {
    navigate(`/tenants/${item.id}/edit`);
  };

  const handleDelete = (item: any) => {
    setSelectedTenant(item);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!selectedTenant) return;
    
    try {
      setIsDeleting(true);
      await tenantService.delete(selectedTenant.id);
      toast.success(t('tenantManagement.tenantDeletedSuccessfully'));
      setShowDeleteConfirm(false);
      setSelectedTenant(null);
      // Reload the table data
      window.location.reload(); // Simple reload to refresh the table
    } catch (error: any) {
      console.error('Error deleting tenant:', error);
      const errorMessage = error?.response?.data?.message || t('tenantManagement.failedToDeleteTenant');
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setSelectedTenant(null);
  };

  return (
    <div className="space-y-6">
      <FlexibleCRUDTable 
        entity={tenantEntity} 
        service={tenantService}
        onCreate={handleCreate}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        showPagination={true}
        showSearch={true}
        onOperationComplete={() => {
          // Table will automatically refresh after operations
        }}
      />

      {/* Delete Confirmation Modal */}
      <Modal 
        isOpen={showDeleteConfirm} 
        onClose={closeDeleteConfirm}
        title={t('tenantManagement.deleteTenant')}
        size="md"
      >
        <div className="space-y-4">
          <p className="text-[var(--color-surface-text)]">
            {t('tenantManagement.confirmDeleteTenant')}
          </p>
          
          {selectedTenant && (
            <div className="bg-[var(--color-secondary-bg)] p-4 rounded-lg border border-[var(--color-border)]">
              <h4 className="font-semibold text-[var(--color-surface-text)] mb-2">
                {t('tenantManagement.tenantDetails')}:
              </h4>
              <div className="space-y-1 text-sm text-[var(--color-secondary-text)]">
                <p><strong>{t('tenantManagement.companyName')}:</strong> {selectedTenant.name}</p>
                <p><strong>{t('tenantManagement.adminEmail')}:</strong> {selectedTenant.admin_email}</p>
                <p><strong>{t('tenantManagement.status')}:</strong> {selectedTenant.status}</p>
              </div>
            </div>
          )}

          <div className="bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-lg p-4">
            <h4 className="font-semibold text-[var(--color-error)] mb-2">
              {t('tenantManagement.warning')}:
            </h4>
            <ul className="text-sm text-[var(--color-error)] space-y-1">
              <li>• {t('tenantManagement.allDataWillBeDeleted')}</li>
              <li>• {t('tenantManagement.allUsersWillBeDeleted')}</li>
              <li>• {t('tenantManagement.actionCannotBeUndone')}</li>
            </ul>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="secondary"
              onClick={closeDeleteConfirm}
              disabled={isDeleting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? t('common.deleting') : t('tenantManagement.deleteTenant')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}; 